import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from 'primereact/checkbox';
import { Skeleton } from 'primereact/skeleton';

import { useGetReceivers } from '../../../queries/receivers';
import { useGetReceiverChannels, useSwitchChannel } from '../../../queries/channels';
import FeatureUnavailable from '../../../components/activation/FeatureUnavailable';
import SourcesPanel from './SourcesPanel';
import { useHasHardware } from '../../../hooks/useHasHardware';
import { useEntitlements } from '../../../hooks/useEntitlements';
import { useCustomToast } from '../../Contexts/ToastContext';

/**
 * Channels grouped by receiver. Click a channel → modal with screen
 * checkboxes → POST /api/channels/{id}/switch. Active channel keeps a
 * purple glow until the user picks another one.
 */
export default function ChannelPanel({ screens, onSwitched }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const fullPtn = useHasHardware();   // Sources panel is a PTN-only surface
  const { has } = useEntitlements();

  const { data: recvData, isLoading: recvLoading } = useGetReceivers({}, { enabled: has('hardware_control') });
  const receivers = Array.isArray(recvData?.data) ? recvData.data
                  : Array.isArray(recvData) ? recvData : [];

  const [picker, setPicker]         = useState(null);   // {channel, receiver} or null
  const [selectedScreens, setSelectedScreens] = useState([]);
  // Optimistic active channel per receiver ({ receiverId: channelId }) so the
  // highlight appears instantly on switch; the persisted receiver.active_channel_id
  // (refetched after the switch invalidates receiversList) is the source of truth
  // on page load / refresh.
  const [optimisticActive, setOptimisticActive] = useState({});

  const switchMut = useSwitchChannel();

  useEffect(() => {
    if (!picker) return;
    // Default selection = all screens
    setSelectedScreens((screens ?? []).map((s) => s.id));
  }, [picker, screens]);

  const toggleAll = (checked) => {
    setSelectedScreens(checked ? (screens ?? []).map((s) => s.id) : []);
  };
  const toggleOne = (id, checked) => {
    setSelectedScreens((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
    );
  };

  const allSelected = (screens ?? []).length > 0 && selectedScreens.length === (screens ?? []).length;

  const doSwitch = () => {
    if (selectedScreens.length === 0) {
      showToast('error', t('remote_control.title'), t('remote_control.channels.no_screens_selected'));
      return;
    }
    switchMut.mutate(
      { id: picker.channel.id, screen_ids: selectedScreens },
      {
        onSuccess: () => {
          showToast('success', t('remote_control.title'),
            t('remote_control.toasts.switched', { channel: picker.channel.name }));
          // Optimistic: highlight this channel on its receiver immediately.
          setOptimisticActive((prev) => ({ ...prev, [picker.receiver.id]: picker.channel.id }));
          onSwitched?.(picker.channel.id, selectedScreens);
          setPicker(null);
        },
      }
    );
  };

  // hardware_control explicitly off: the calm card instead of an empty state that asks to add receivers.
  if (!has('hardware_control')) {
    return (
      <div className="remote-section">
        <div className="remote-section-title">
          <i className="pi pi-list" /> {t('remote_control.sections.channels')}
        </div>
        <FeatureUnavailable compact />
      </div>
    );
  }

  if (recvLoading) {
    return (
      <div className="remote-section">
        <div className="remote-section-title">
          <i className="pi pi-list" /> {t('remote_control.sections.channels')}
        </div>
        <Skeleton height="3rem" className="mb-2" />
        <Skeleton height="3rem" className="mb-2" />
        <Skeleton height="3rem" />
      </div>
    );
  }

  if (!receivers.length) {
    return (
      <div className="remote-section">
        <div className="remote-section-title">
          <i className="pi pi-list" /> {t('remote_control.sections.channels')}
        </div>
        <p className="text-muted small mb-0">{t('remote_control.channels.empty')}</p>
      </div>
    );
  }

  return (
    <div className="remote-section">
      <div className="remote-section-title">
        <i className="pi pi-list" /> {t('remote_control.sections.channels')}
      </div>

      <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
        {receivers.map((receiver) => (
          <ReceiverBlock
            key={receiver.id}
            receiver={receiver}
            optimisticChannelId={optimisticActive[receiver.id]}
            onPick={(channel) => setPicker({ channel, receiver })}
          />
        ))}

        {/* Sources panel — sits under the channels list so the left rail
            holds both "switch a channel" (blue/purple) and "route a
            source" (green) flows. */}
        {fullPtn && <SourcesPanel screens={screens} />}
      </div>

      <Dialog
        header={
          picker
            ? t('remote_control.channels.switch_to', { channel: picker.channel.name })
            : ''
        }
        visible={!!picker}
        onHide={() => setPicker(null)}
        style={{ width: 'min(95vw, 420px)' }}
        modal
      >
        <p className="small text-muted mb-3">{t('remote_control.channels.switch_prompt')}</p>

        <div className="d-flex flex-column gap-2 mb-3">
          <label className="d-flex align-items-center gap-2 fw-semibold">
            <Checkbox
              checked={allSelected}
              onChange={(e) => toggleAll(e.checked)}
            />
            {t('remote_control.channels.all_screens')}
          </label>

          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {(screens ?? []).map((s) => (
            <label key={s.id} className="d-flex align-items-center gap-2">
              <Checkbox
                checked={selectedScreens.includes(s.id)}
                onChange={(e) => toggleOne(s.id, e.checked)}
              />
              <span>{s.name}</span>
              {s.location && <span className="text-muted small">— {s.location}</span>}
            </label>
          ))}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <Button
            label={t('buttons.cancel')}
            severity="secondary"
            outlined
            onClick={() => setPicker(null)}
          />
          <Button
            label={t('remote_control.channels.switch')}
            loading={switchMut.isLoading}
            onClick={doSwitch}
          />
        </div>
      </Dialog>
    </div>
  );
}

/* ── Sub-component: a receiver's channel grid ────────────────────────── */

function ReceiverBlock({ receiver, optimisticChannelId, onPick }) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetReceiverChannels(receiver.id);
  const channels = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];

  // Active channel = optimistic (just clicked) if present, else the persisted
  // receiver.active_channel_id from the backend (survives page refresh).
  const activeId = optimisticChannelId ?? receiver.active_channel_id ?? null;

  return (
    <div className="remote-receiver-block">
      <p className="remote-receiver-name">
        <i className="pi pi-satellite me-1" />
        {receiver.name}
      </p>

      {isLoading ? (
        <Skeleton height="2.5rem" />
      ) : channels.length === 0 ? (
        <p className="small text-muted mb-2">—</p>
      ) : (
        <div className="remote-channel-grid">
          {channels.map((ch) => {
            const active = ch.id === activeId;
            return (
              <button
                key={ch.id}
                type="button"
                className={`remote-channel-btn ${active ? 'remote-channel-btn-active' : ''}`}
                onClick={() => onPick(ch)}
                title={ch.name}
              >
                {ch.logo_url && (
                  <img src={ch.logo_url} alt="" className="remote-channel-logo"
                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                )}
                <span className="remote-channel-name">{ch.name}</span>
                {ch.channel_number && (
                  <span className="remote-channel-number">{t('remote_control.channels.ch')} {ch.channel_number}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
